import React from 'react';
import IconSystem from 'icon-collection/icon-systems';

// ============================================================================
// The modal frame the Settings administration modals share (Roles & Permissions,
// User Access): the house header with Minimize-to-bar and Close, underlined tabs,
// a scrolling body, a footer, and the "Saving…" overlay.
// ============================================================================

export interface ModalTab<T extends string> { id: T; label: string; count?: number }

interface Props<T extends string> {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose: () => void;
  /** Shown only when given (the owning screen has a tab to minimize into). */
  onMinimize?: () => void;
  tabs?: ModalTab<T>[];
  activeTab?: T;
  onTabChange?: (t: T) => void;
  footer: React.ReactNode;
  busy?: boolean;
  busyLabel?: string;
  children: React.ReactNode;
}

export function SettingsModalFrame<T extends string>({
  title, subtitle, onClose, onMinimize, tabs, activeTab, onTabChange, footer, busy, busyLabel = 'Saving…', children,
}: Props<T>) {
  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxWidth: 1100, width: '95vw', height: '86vh', display: 'flex', flexDirection: 'column' }}>
        {busy && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{busyLabel}</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="modal-title">{title}</h2>
            {subtitle && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>{subtitle}</div>}
          </div>
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            {onMinimize && (
              <button className="modal-header-btn" onClick={onMinimize} title="Minimize to bar"><IconSystem.chevronDown size={16} /></button>
            )}
            <button className="modal-close" onClick={onClose} disabled={busy}><IconSystem.x size={20} /></button>
          </div>
        </div>

        {tabs && activeTab && onTabChange && (
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', padding: '0 1rem', flexShrink: 0 }}>
            {tabs.map(t => (
              <button key={t.id} type="button" onClick={() => onTabChange(t.id)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '0.55rem 0.9rem', fontSize: '0.85rem',
                  color: activeTab === t.id ? 'var(--primary)' : 'var(--text-primary)', fontWeight: activeTab === t.id ? 600 : 500,
                  borderBottom: `2px solid ${activeTab === t.id ? 'var(--primary)' : 'transparent'}`, marginBottom: -1,
                }}>
                {t.label}{t.count !== undefined && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 6 }}>{t.count}</span>}
              </button>
            ))}
          </div>
        )}

        <div className="modal-content" style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '0.75rem', color: 'var(--text-primary)' }}>
          {children}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>{footer}</div>
      </div>
    </>
  );
}

/** Label / value rows for read-only details. */
export const DetailList: React.FC<{ rows: [string, React.ReactNode][] }> = ({ rows }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(170px, 220px) 1fr', columnGap: '1.5rem' }}>
    {rows.map(([label, value]) => (
      <React.Fragment key={label}>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.45rem 0', borderBottom: '1px solid var(--border-color)' }}>{label}</div>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', padding: '0.45rem 0', borderBottom: '1px solid var(--border-color)' }}>{value}</div>
      </React.Fragment>
    ))}
  </div>
);
